// TimeBar (timesheet round): a part of a technician's clocked day, placed along a timeline (timesheet
// rules §3, §10). Status is drawn, never coloured: work is hatched in the person's pen with the
// customer's name; a drive is half height and has no words; shop and supply house are a muted hatch
// with their word; lunch is no box, a muted baseline and "Lunch, not paid"; running now is solid pen;
// no clock-out is a red dashed outline running on to the strip's end with "?"; to check is a red line
// and red words; a change not saved yet is a dashed ink ghost over it; the chosen one has the heavier
// line. It's placed by its own from and to, fractions of its track (TimeTrack works them out from
// minutes), and fills the row its track gives it. Pressable or still. Reads only --tbar-*.
import type { CSSProperties } from 'react';
import { Button } from 'react-aria-components';
import './TimeBar.css';

export type TimeBarKind = 'work' | 'drive' | 'lunch' | 'shop' | 'supply_house';
/** done: it ended; running: open today, still going; noEnd: open on an earlier day, a missed clock-out. */
export type TimeBarState = 'done' | 'running' | 'noEnd';
/** 1 to 8, a technician's pen (a pen identifies a person, never a state). */
export type TimeBarPen = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface TimeBarProps {
  kind: TimeBarKind;
  /** Where it starts and ends along its track, 0 to 1. A part with no end runs on to 1. */
  from: number;
  to: number;
  /** Its words: the customer's name, "Shop", "Supply house"; a drive has none, lunch its own. */
  label?: string;
  /** Its name in full, for a screen reader: "Work at Whitford, 8:30 a.m. to 3 p.m." */
  name: string;
  pen?: TimeBarPen | null;
  state?: TimeBarState;
  /** Why payroll should look at it, in words: a red line and red words. */
  toCheck?: string | null;
  /** Lunch and other unpaid time say so. */
  paid?: boolean;
  isSelected?: boolean;
  /** A change not saved yet: a dashed ink outline over the part, the way a drag's ghost is drawn. */
  isGhost?: boolean;
  /** Pressing it chooses the part; without it, it's still (the Timesheet's rows, a locked day). */
  onPress?: () => void;
}

const WORDS: Record<TimeBarKind, string> = {
  work: '',
  drive: '',
  lunch: 'Lunch',
  shop: 'Shop',
  supply_house: 'Supply house',
};

export function TimeBar({
  kind,
  from,
  to,
  label,
  name,
  pen,
  state = 'done',
  toCheck,
  paid = true,
  isSelected,
  isGhost,
  onPress,
}: TimeBarProps) {
  const start = clamp(from);
  const end = state === 'noEnd' ? 1 : Math.max(clamp(to), start);
  const style = {
    insetInlineStart: `${start * 100}%`,
    inlineSize: `${(end - start) * 100}%`,
    ...(pen ? { '--tbar-pen': `var(--tbar-pen-${pen})` } : {}),
  } as CSSProperties;
  const words =
    kind === 'drive'
      ? ''
      : kind === 'lunch'
        ? `${WORDS.lunch}${paid ? '' : ', not paid'}`
        : `${label ?? WORDS[kind]}${kind !== 'work' && !paid ? ', not paid' : ''}`;
  const data = {
    'data-kind': kind,
    'data-state': state,
    'data-check': toCheck ? '' : undefined,
    'data-selected': isSelected ? '' : undefined,
  };
  const face = (
    <>
      {words ? <span className="fl-tbar__words">{words}</span> : null}
      {state === 'noEnd' ? (
        <span className="fl-tbar__open" aria-hidden="true">
          ?
        </span>
      ) : null}
    </>
  );
  const full = [name, state === 'noEnd' ? 'no clock-out' : null, toCheck ? `to check: ${toCheck}` : null]
    .filter(Boolean)
    .join(', ');

  if (isGhost)
    return (
      <span className="fl-tbar" data-kind={kind} data-ghost="" style={style} aria-hidden="true">
        {words ? <span className="fl-tbar__words">{words}</span> : null}
      </span>
    );
  if (onPress)
    return (
      <Button
        className="fl-tbar"
        {...data}
        style={style}
        aria-label={full}
        aria-pressed={isSelected ? true : undefined}
        onPress={onPress}
      >
        {face}
      </Button>
    );
  return (
    <span className="fl-tbar" {...data} style={style} role="img" aria-label={full}>
      {face}
    </span>
  );
}

const clamp = (x: number) => Math.min(Math.max(Number.isFinite(x) ? x : 0, 0), 1);
